import { Tooltip } from '@mui/material';
import dayjs from 'dayjs';
import { memo } from 'react';
import { DeliveryContainerUtilisationModel } from '../../../../../../features/reports/delivery-container-utilisation/delivery-container-utilisation-props.map';
import { LoadingIndicator } from '../../../loading-indicator/loading-indicator.component';
import { DELIVERY_CONTAINER_UTILISATION_Y_AXIS_WIDTH } from '../delivery-container-capacity-chart/delivery-container-capacity-chart.component';
import { useDeliveryContainerUtilisationChartStyles } from './delivery-container-utilisation-chart.styles';

export interface DeliveryContainerUtilisationChartProps {
  isLoading: boolean;
  model: DeliveryContainerUtilisationModel;
  /**
   * When true, drop the day-header row and the in-cell percentage text. The page sets
   * this when the requested range is longer than one month — otherwise the labels
   * become illegibly cramped.
   */
  compact?: boolean;
}

export const DeliveryContainerUtilisationChart = memo(function DeliveryContainerUtilisationChart({
  isLoading,
  model,
  compact = false
}: DeliveryContainerUtilisationChartProps) {
  const classes = useDeliveryContainerUtilisationChartStyles();
  const { days, rows } = model;
  const gridTemplateColumns = `${DELIVERY_CONTAINER_UTILISATION_Y_AXIS_WIDTH}px repeat(${Math.max(
    days.length,
    1
  )}, minmax(0, 1fr))`;

  return (
    <div className={classes.wrapper}>
      {isLoading && (
        <div className={classes.loading}>
          <LoadingIndicator />
        </div>
      )}
      <div className={classes.grid} style={{ gridTemplateColumns }}>
        {!compact && (
          <>
            <div className={classes.cornerCell} />
            {days.map((day) => (
              <div key={day.ms} className={classes.dayHeader} title={dayjs(day.ms).format('ddd D MMM YYYY')}>
                {dayjs(day.ms).format('ddd D MMM')}
              </div>
            ))}
          </>
        )}
        {rows.map((row) => (
          <Row key={row.id} row={row} compact={compact} />
        ))}
      </div>
    </div>
  );
});

function Row({ row, compact }: { row: DeliveryContainerUtilisationModel['rows'][number]; compact: boolean }) {
  const classes = useDeliveryContainerUtilisationChartStyles();
  return (
    <>
      <Tooltip title={row.name} placement="top" classes={{ tooltip: classes.tooltip }}>
        <div className={classes.groupLabel}>
          <span className={classes.groupLabelText}>{row.name}</span>
        </div>
      </Tooltip>
      {row.cells.map((cell) => {
        const tooltipText = formatTooltip(row.name, cell);
        const cellTextColour = cell.band != null && cell.band >= 70 ? '#FFFFFF' : '#222';
        const label = !compact && cell.percent != null ? `${cell.percent}%` : '';
        return (
          <Tooltip key={cell.dayMs} title={tooltipText} arrow placement="top" classes={{ tooltip: classes.tooltip }}>
            <div className={classes.cell} style={{ backgroundColor: cell.fill, color: cellTextColour }}>
              {label}
            </div>
          </Tooltip>
        );
      })}
    </>
  );
}

function formatTooltip(groupName: string, cell: DeliveryContainerUtilisationModel['rows'][number]['cells'][number]) {
  const date = dayjs(cell.dayMs).format('ddd D MMM');
  if (cell.rate == null) {
    return `${groupName} • ${date} • no data`;
  }
  return `${groupName} • ${date} • ${cell.percent}% (${cell.rate.toFixed(2)}/locker)`;
}
